import { auth } from '../firebase'; // Import auth for user authentication
import { terminology } from '../config/terminology'; // Import terminology for dynamic labels

// Define API_BASE_URL (ensure this matches your environment setup)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';

/**
 * Searches for households (clients in terminology) based on a search string.
 * Note: The example code mentioned an optional clientId (Supplier ID) filter,
 * which is not typically relevant when a franchisee searches for their households.
 * This implementation focuses on searching by string for the logged-in user's context.
 * If filtering by Supplier (Client in terminology) is needed later, the API and this function can be updated.
 *
 * @param {object} options - Search options.
 * @param {string} options.searchString - The string to search for (name, address, etc.).
 * @param {number} [options.pageSize=50] - Number of results to return.
 * @returns {Promise<object>} - The response data containing matching households { households: [], nextPageDocId: null }.
 * @throws {Error} - Throws an error if the search fails or the user is not authenticated.
 */
export const searchHouseholds = async ({ searchString, pageSize = 50 }) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated.");
    }
    if (!searchString || searchString.length < 3) {
        // Return consistent structure for short/empty search strings
        return { households: [], nextPageDocId: null };
    }

    try {
        const idToken = await user.getIdToken();
        const url = `${API_BASE_URL}/household/search`; // Assuming this is the correct endpoint

        // Determine userType based on custom claims if available, otherwise default or handle error
        // For this context (franchisee app), userType might be implicitly 'franchisee' on the backend
        // Or you might need to fetch user info first if the backend requires it in the payload.
        // Let's assume the backend infers user type from the token or doesn't strictly need it for this search.
        const payload = {
            searchString: searchString,
            pageSize: pageSize,
            // userType: 'franchisee', // Include if backend requires it
        };

        console.log("API Request POST to /household/search with payload:", JSON.stringify(payload));

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const responseData = await response.json();
        console.log("API Response from /household/search:", responseData);


        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }
        if (responseData.status !== 'success') {
            throw new Error(responseData.message || `Failed to search ${terminology.household.plural.toLowerCase()}`);
        }

        // Ensure consistent return structure
        return {
            households: responseData.data?.households || [],
            nextPageDocId: responseData.data?.nextPageDocId || null // Adapt if backend provides this
        };

    } catch (error) {
        console.error(`Error searching ${terminology.household.plural.toLowerCase()}:`, error);
        // Return consistent structure on error, including the error message
        return { households: [], nextPageDocId: null, error: error.message };
    }
};

/**
 * Fetches details for a specific household (client in terminology) by ID.
 * @param {string} householdId - The ID of the household to fetch.
 * @returns {Promise<object>} - The household data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getHouseholdById = async (householdId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!householdId) {
    throw new Error(`${terminology.household.singular} ID is required.`);
  }

  try {
    const idToken = await user.getIdToken();
    // Assuming the endpoint requires POST and householdId in the body
    const url = `${API_BASE_URL}/household/get-by-id`; // Adjust endpoint if needed
    const payload = { householdId };

    console.log("API Request POST to /household/get-by-id with payload:", JSON.stringify(payload));


    const response = await fetch(url, {
      method: 'POST', // Or GET if the API uses /household/:id
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json', // Needed for POST with body
      },
      body: JSON.stringify(payload), // Send ID in body for POST
    });

    const responseData = await response.json();
    console.log("API Response from /household/get-by-id:", responseData);


    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to fetch ${terminology.household.singular.toLowerCase()} details`);
    }

    // Assuming data is nested under 'data' key in the response
    return responseData.data;

  } catch (error) {
    console.error(`Error fetching ${terminology.household.singular.toLowerCase()} ${householdId}:`, error);
    throw error; // Re-throw the error for the calling component to handle
  }
};
